import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import translations from '../i18n/translations';
import { useUserData } from './UserDataContext';
import { apiRequest } from '../utils/api';

const LanguageContext = createContext(null);
LanguageContext.displayName = 'LanguageContext';

const SUPPORTED = ['es', 'en'];
const STORAGE_KEY = 'pf_lang';

const FLAG_ES = 'https://iili.io/CdYQ3F4.png';
const FLAG_EN = 'https://iili.io/CdYZcdl.md.png';

export { FLAG_ES, FLAG_EN };

function detectStoredLang() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;
  return null;
}

/**
 * Idioma inicial síncrono (sin red):
 *  1. Preferencia guardada manualmente → máxima prioridad
 *  2. IP detectada en sesión anterior (caché)
 *  3. Idioma del navegador como aproximación rápida
 *  4. Español por defecto
 */
function getSyncLang() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;

  const cachedIp = localStorage.getItem(IP_LANG_KEY);
  if (cachedIp && SUPPORTED.includes(cachedIp)) return cachedIp;

  try {
    const nav = (navigator.language || navigator.userLanguage || '').toLowerCase();
    if (nav.startsWith('en')) return 'en';
  } catch { /* ignore */ }

  return 'es';
}

async function detectLangByIp() {
  try {
    const res = await fetch('https://ipapi.co/country_code/', { signal: AbortSignal.timeout(4000) });
    if (!res.ok) throw new Error('ip-api error');
    const code = (await res.text()).trim().toUpperCase();
    return code === 'US' ? 'en' : 'es';
  } catch {
    return 'es';
  }
}

const IP_LANG_KEY = 'pf_ip_lang';

export function LanguageProvider({ children }) {
  const { userData, updateUserData } = useUserData();

  const [lang, setLangState] = useState(() => getSyncLang());
  const [detecting, setDetecting] = useState(() => !detectStoredLang());
  // ipLang: idioma detectado por IP — determina el ORDEN de banderas, no la selección activa
  const [ipLang, setIpLang] = useState(
    () => localStorage.getItem(IP_LANG_KEY) || null
  );
  const detectedRef = useRef(false);

  // On first mount with no stored preference, detect by IP
  useEffect(() => {
    if (detectedRef.current) return;
    detectedRef.current = true;

    // Si ya tenemos el ipLang guardado, no volver a llamar a la API
    const storedIpLang = localStorage.getItem(IP_LANG_KEY);
    if (storedIpLang) {
      setIpLang(storedIpLang);
    }

    // Si ya hay idioma guardado (preferencia manual), solo detectamos para el orden
    const alreadyStored = detectStoredLang();
    if (alreadyStored && storedIpLang) {
      return; // Tenemos todo, no hay que llamar a la API
    }

    setDetecting(true);

    detectLangByIp().then((detected) => {
      // Guardar el idioma de IP (solo para ordenar banderas)
      if (!storedIpLang) {
        setIpLang(detected);
        localStorage.setItem(IP_LANG_KEY, detected);
      }

      // Si no había preferencia guardada, usarla como idioma activo
      if (!alreadyStored) {
        const fromUserData = userData?.idioma;
        const final = (fromUserData && SUPPORTED.includes(fromUserData)) ? fromUserData : detected;
        setLangState(final);
        localStorage.setItem(STORAGE_KEY, final);
      }

      setDetecting(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep document.documentElement.lang in sync
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Sync with userData.idioma on login — ONLY when the user has no local preference.
  // A local preference (set manually) always wins over the backend value.
  useEffect(() => {
    const idioma = userData?.idioma;
    if (!idioma || !SUPPORTED.includes(idioma)) return;

    // If the user manually set a preference, respect it and don't override
    const hasLocalPref = Boolean(localStorage.getItem(STORAGE_KEY));
    if (!hasLocalPref && idioma !== lang) {
      setLangState(idioma);
      localStorage.setItem(STORAGE_KEY, idioma);
    }
  }, [userData?.idioma]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLanguage = useCallback((newLang) => {
    if (!SUPPORTED.includes(newLang)) return;

    // Apply immediately and persist locally
    setLangState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);

    // Keep in-memory userData in sync
    if (userData) {
      updateUserData({ ...userData, idioma: newLang });
    }

    // Persist to backend using the self-update endpoint (no admin notifications)
    apiRequest('/actualizar-datos-usuario', {
      method: 'PUT',
      body: JSON.stringify({ idioma: newLang }),
    }).catch((err) => {
      console.warn('[LanguageContext] No se pudo guardar idioma en el servidor:', err.message);
    });
  }, [userData, updateUserData]);

  // Translation function with optional template variables
  const t = useCallback((key, vars) => {
    const dict = translations[lang] || translations.es;
    let str = dict[key];
    if (str === undefined) {
      // Fallback to Spanish
      str = translations.es[key];
    }
    if (str === undefined) return key;
    if (vars && typeof str === 'string') {
      Object.entries(vars).forEach(([k, v]) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      });
    }
    return str;
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    setLanguage,
    t,
    detecting,
    /** Idioma detectado por IP: solo para determinar el orden de banderas */
    ipLang: ipLang || lang,
    FLAG_ES,
    FLAG_EN,
  }), [lang, setLanguage, t, detecting, ipLang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}

export default LanguageContext;
